import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { SiteFilter } from "@/components/admin/SiteFilter";
import { NetworkVisualization } from "@/components/admin/NetworkVisualization";
import { 
  Network, Users, AlertTriangle, Award, RefreshCw, 
  Loader2, Link2, TrendingUp, Activity 
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface NetworkStats {
  totalWorkers: number;
  totalConnections: number;
  champions: number;
  bridges: number;
  isolates: number;
  avgCentrality: number;
}

interface AdminNetworkMetricsProps {
  siteId?: string | null;
}

const AdminNetworkMetrics = ({ siteId: propSiteId }: AdminNetworkMetricsProps = {}) => {
  const [selectedSite, setSelectedSite] = useState<string>(propSiteId || 'all');
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [stats, setStats] = useState<NetworkStats>({
    totalWorkers: 0,
    totalConnections: 0,
    champions: 0,
    bridges: 0,
    isolates: 0,
    avgCentrality: 0
  });

  useEffect(() => {
    if (propSiteId) {
      setSelectedSite(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    fetchStats();
  }, [selectedSite]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("worker_profiles")
        .select("company_id")
        .eq("user_id", session.user.id)
        .single();

      if (!profile?.company_id) return;

      const siteFilter = selectedSite !== 'all' ? selectedSite : null;

      // Build queries with optional site filter
      let workersQuery = supabase
        .from("worker_profiles")
        .select("id, network_role, network_centrality")
        .eq("company_id", profile.company_id);
      
      let connectionsQuery = supabase
        .from("worker_network_connections")
        .select("*", { count: "exact", head: true })
        .eq("company_id", profile.company_id);

      if (siteFilter) {
        workersQuery = workersQuery.eq("site_id", siteFilter);
      }

      const [{ data: workers }, { count: connections }] = await Promise.all([
        workersQuery,
        connectionsQuery,
      ]);

      const workersList = workers || [];
      const champions = workersList.filter(w => w.network_role === 'champion').length;
      const bridges = workersList.filter(w => w.network_role === 'bridge').length;
      const isolates = workersList.filter(w => w.network_role === 'isolate').length;
      
      const centralityValues = workersList
        .map(w => w.network_centrality || 0)
        .filter(c => c > 0);
      const avgCentrality = centralityValues.length > 0
        ? centralityValues.reduce((a, b) => a + b, 0) / centralityValues.length
        : 0;

      setStats({
        totalWorkers: workersList.length,
        totalConnections: connections || 0,
        champions,
        bridges,
        isolates,
        avgCentrality: Math.round(avgCentrality * 100) / 100
      });
    } catch (error) {
      console.error("Error fetching network stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateNetworkMetrics = async () => {
    setCalculating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("worker_profiles")
        .select("company_id")
        .eq("user_id", session.user.id)
        .single();

      if (!profile?.company_id) return;

      const { data: workers } = await supabase
        .from("worker_profiles")
        .select("id")
        .eq("company_id", profile.company_id);

      if (!workers || workers.length === 0) {
        toast.info("No workers found to calculate metrics for");
        return;
      }

      toast.info(`Calculating network metrics for ${workers.length} workers...`);

      for (const worker of workers) {
        await supabase.functions.invoke("calculate-network-metrics", { 
          body: { workerId: worker.id } 
        });
      }

      toast.success("Network metrics calculated successfully");
      await fetchStats();
    } catch (error) {
      console.error("Error calculating network metrics:", error);
      toast.error("Failed to calculate network metrics");
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Network className="h-6 w-6 text-primary" />
            Network Metrics
          </h2>
          <p className="text-muted-foreground">
            Analyze worker connections and influence patterns
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!propSiteId && (
            <SiteFilter 
              value={selectedSite} 
              onChange={setSelectedSite}
              className="w-[200px]"
            />
          )}
          <Button
            variant="outline"
            onClick={calculateNetworkMetrics}
            disabled={calculating}
            className="gap-2"
          >
            {calculating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Calculate Metrics
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            Workers
          </div>
          <div className="text-2xl font-bold mt-1">{stats.totalWorkers}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link2 className="h-4 w-4 text-primary" />
            Connections
          </div>
          <div className="text-2xl font-bold mt-1">{stats.totalConnections}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-success/10 to-success/5 border-success/20">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Award className="h-4 w-4 text-success" />
            Champions
          </div>
          <div className="text-2xl font-bold mt-1 text-success">{stats.champions}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Network className="h-4 w-4 text-primary" />
            Bridges
          </div>
          <div className="text-2xl font-bold mt-1 text-primary">{stats.bridges}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-destructive/10 to-destructive/5 border-destructive/20">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Isolates
          </div>
          <div className="text-2xl font-bold mt-1 text-destructive">{stats.isolates}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Activity className="h-4 w-4" />
            Avg Centrality
          </div>
          <div className="text-2xl font-bold mt-1">{stats.avgCentrality}</div>
        </Card>
      </div>

      {/* Network Visualization */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-2">Worker Network Map</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Interactive network visualization showing worker connections, roles, and influence levels
        </p>
        <NetworkVisualization siteId={propSiteId || (selectedSite !== 'all' ? selectedSite : undefined)} />
      </Card>
    </div>
  );
};

export default AdminNetworkMetrics;